<template>
  <v-card class="pa-6" flat>
    <v-form ref="form" v-model="valid">
      <!-- Title Section -->
      <h3 class="title mb-6">Edit Tenant</h3>
      
      <!-- Search Section -->
      <v-row>
        <v-col cols="12" md="8">
          <div class="text-subtitle-1 black--text mb-2">Search for tenant to edit</div>
          <Search 
            label="Search by name"
            @selectedOID="fetchTenant" 
            @clearItem="handleClearSearch"
            endpoint="tenant"
            myParams="name"
          />
        </v-col>
      </v-row>

      <!-- Selected Tenant Card -->
      <v-card v-if="selectedTenantID && !fetchingTenant" class="mt-4 mb-6" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-1">Currently Editing:</div>
          <div class="text-body-1">{{ displayTenantName }}</div>
          <div class="text-caption">Tenant ID: {{ selectedTenantID }}</div>
        </v-card-text>
      </v-card>

      <!-- Loading Progress -->
      <v-progress-linear v-if="fetchingTenant" indeterminate color="secondary" class="mb-4"></v-progress-linear>

      <!-- Tenant Details Form -->
      <div v-if="selectedTenantID">
        <v-divider class="mb-6"></v-divider>
        
        <!-- Personal Details Section -->
        <h4 class="subtitle-1 mb-4">Personal Details</h4>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              v-model="title"
              :items="titles"
              label="Title"
              outlined
              dense
              :disabled="fetchingTenant"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="firstName"
              label="First Name"
              outlined
              dense
              :disabled="fetchingTenant"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="surname"
              label="Surname"
              outlined
              dense
              :disabled="fetchingTenant"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="email"
              label="Email"
              outlined
              dense
              type="email"
              :disabled="fetchingTenant"
              :rules="emailRules"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="mobile"
              label="Mobile Phone"
              outlined
              dense
              :disabled="fetchingTenant"
              :rules="phoneRules"
            />
          </v-col>
        </v-row>

        <!-- Bank Details Section (Read-only) -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">
          Bank Details
          <v-chip x-small class="ml-2" color="grey lighten-2">Read Only</v-chip>
        </h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="accountName"
              label="Account Name"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="account"
              label="Account Number"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="sortCode"
              label="Sort Code"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="statementRef"
              label="Statement Reference"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
        </v-row>

        <!-- Account Information Section (Read-only) -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">
          Account Information
          <v-chip x-small class="ml-2" color="grey lighten-2">Read Only</v-chip>
        </h4>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="MainAccountBalance"
              label="Main Account Balance"
              outlined
              dense
              readonly
              filled
              prefix="£"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="RentOutstanding"
              label="Outstanding Rent"
              outlined
              dense
              readonly
              filled
              prefix="£"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="DepositHeld"
              label="Deposit Held"
              outlined
              dense
              readonly
              filled
              prefix="£"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="Comments"
              label="Comments"
              outlined
              dense
              readonly
              filled
              rows="2"
            />
          </v-col>
        </v-row>
        
        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-right">
            <v-btn
              text
              @click="handleClearSearch"
              :disabled="loading"
              class="mr-3"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="loading"
              :disabled="!valid || fetchingTenant"
              @click="saveTenant"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-2">mdi-account-search</v-icon>
        <p class="text-subtitle-1 grey--text mt-3">Search for a tenant above to start editing</p>
      </div>
    </v-form>

    <!-- Snackbar for notifications -->
    <Snackbar 
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :timeout="timeout"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
    />
  </v-card>
</template>

<script>
import { amplifyProxy } from '@/services/directApi'
import Search from '@/components/CloudSearch/Search'
import Snackbar from '@/components/Snackbars/TheSnackbar'

export default {
  components: {
    Search,
    Snackbar
  },
  data() {
    return {
      valid: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loading: false,
      fetchingTenant: false,
      selectedTenantID: null,
      title: '',
      firstName: '',
      middlename: '',
      surname: '',
      mobile: '',
      email: '',
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid',
      ],
      phoneRules: [
        v => !!v || 'Mobile phone is required',
        v => /^[\d\s+()-]+$/.test(v) || 'Phone number must be valid'
      ],
      accountName: '',
      account: '',
      sortCode: '',
      statementRef: '',
      // Read only fields
      MainAccountBalance: '',
      RentOutstanding: '',
      DepositHeld: '',
      Comments: '',
      titles: ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof', 'Rev', 'Other'],
      rules: {
        required: value => !!value || 'Required field'
      }
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
    },

    handleClearSearch() {
      this.selectedTenantID = null
      this.clearForm()
    },

    async fetchTenant(id) {
      this.selectedTenantID = id
      this.clearForm()
      this.fetchingTenant = true

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/tenants/${id}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data

        // Personal details
        this.title = data.Name?.Title || ''
        this.firstName = data.Name?.Forename || ''
        this.surname = data.Name?.Surname || ''
        this.email = data.Email || ''
        this.mobile = data.Mobile || ''

        // Bank details
        this.accountName = data.BankAccount?.AccountName || ''
        this.account = data.BankAccount?.AccountNumber || ''
        this.sortCode = data.BankAccount?.SortCode || ''
        this.statementRef = data.BankAccount?.LegacyBacsReference || ''

        // Account information
        this.MainAccountBalance = data.MainAccountBalance || '0.00'
        this.RentOutstanding = data.RentOutstanding || '0.00'
        this.DepositHeld = data.DepositHeld || '0.00'
        this.Comments = data.Comments || ''
      } catch (error) {
        console.error('Error fetching tenant:', error)
        this.snackbar = true
        this.snackbarText = 'Error fetching tenant details: ' + (error.response?.data?.message || error.message)
        this.snackbarColor = 'red'
      } finally {
        this.fetchingTenant = false
      }
    },

    async saveTenant() {
      this.loading = true

      const tenantObject = {
        Name: {
          Title: this.title,
          Forename: this.firstName,
          Surname: this.surname
        },
        Mobile: this.mobile,
        Email: this.email,
        BankAccount: {
          AccountName: this.accountName,
          AccountNumber: this.account,
          SortCode: this.sortCode,
          LegacyBacsReference: this.statementRef
        }
      }

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/tenants/${this.selectedTenantID}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        await amplifyProxy.patch(endpoint, tenantObject, options)

        this.snackbar = true
        this.snackbarText = 'Tenant update successful'
        this.snackbarColor = 'green'
        await this.fetchTenant(this.selectedTenantID)
      } catch (error) {
        this.snackbar = true
        this.snackbarText = 'ERROR: ' + (error.response?.data || error.message)
        this.snackbarColor = 'red'
        await this.fetchTenant(this.selectedTenantID)
      } finally {
        this.loading = false
      }
    },

    clearForm() {
      this.title = ''
      this.firstName = ''
      this.surname = ''
      this.email = ''
      this.mobile = ''
      this.accountName = ''
      this.account = ''
      this.sortCode = ''
      this.statementRef = ''
      this.MainAccountBalance = ''
      this.RentOutstanding = ''
      this.DepositHeld = ''
      this.Comments = ''
    }
  },
  computed: {
    displayTenantName() {
      if (!this.firstName && !this.surname) return 'No tenant selected'
      return `${this.title} ${this.firstName} ${this.surname}`.trim()
    }
  }
}
</script>